import { Carousel, Empty, Image, Typography } from "antd";
import { FC } from "react";
import { X, Package, Truck } from "lucide-react";
import HistoryServiceTickets from "~/components/client/inventory/history-service-tickets";
import { TInventory } from "~/types";

const { Text } = Typography;

interface InventoryPhotoHistorySidebarProps {
  inventory: TInventory;
  onClose: () => void;
}

export const InventoryPhotoHistorySidebar: FC<InventoryPhotoHistorySidebarProps> = ({
  inventory,
  onClose,
}) => {

  if (!inventory) {
    return null;
  }

  const hasImages = inventory.images && inventory.images.length > 0;

  return (
    <div className="h-full flex flex-col bg-white border-l border-[#e5e5e5]">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[#e5e5e5] bg-white">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Package size={20} className="text-[#1a1a2e] flex-shrink-0" />
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-[#1a1a2e] uppercase tracking-wide !mb-0 truncate">
              {inventory.inventoryRef || "Photos & History"}
            </h2>
            {inventory.title && (
              <Text className="text-xs text-[#1a1a2e] truncate block mt-0.5">
                {inventory.title}
              </Text>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-[#fafafa] transition-colors flex-shrink-0 ml-2"
          aria-label="Close sidebar"
        >
          <X size={20} className="text-[#1a1a2e]" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Image Carousel */}
        {hasImages ? (
          <div className="bg-white p-4 border-b border-[#e5e5e5]">
            <div className="inventory-carousel bg-white border border-[#e5e5e5] rounded-lg overflow-hidden">
              <Carousel autoplay autoplaySpeed={4000} arrows>
                {inventory.images.map((image, index) => (
                  <div key={image.id || index}>
                    <div className="h-64 flex items-center justify-center bg-white">
                      <Image
                        src={image.url}
                        alt={`${inventory.title} - Image ${index + 1}`}
                        className="max-h-64 object-contain"
                        style={{ maxHeight: "256px" }}
                      />
                    </div>
                  </div>
                ))}
              </Carousel>
            </div>
            <Text className="text-xs mt-2 block text-center text-[#1a1a2e] font-bold uppercase tracking-wide">
              {inventory.images.length} image{inventory.images.length > 1 ? "s" : ""}
            </Text>
          </div>
        ) : (
          <div className="bg-white p-8 border-b border-[#e5e5e5]">
            <Empty
              description={
                <span className="text-[#1a1a2e] font-bold uppercase tracking-wide text-sm">
                  No photos available
                </span>
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        )}

        {/* Service Tickets */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Truck size={16} className="text-[#1a1a2e]" />
            <Text strong className="text-[#1a1a2e] text-xs uppercase tracking-wide">
              Service Tickets
            </Text>
          </div>
          <div className="border border-[#e5e5e5] rounded-lg bg-white">
            <HistoryServiceTickets inventoryId={inventory.id} />
          </div>
        </div>
      </div>
    </div>
  );
};

